/**
 * AttachmentsWidget — reusable file upload/download component
 * Usage: <AttachmentsWidget entityType="lc_sblc" entityId={record.id} />
 */
import React, { useState, useEffect, useRef } from 'react';
import { API } from '../App';

const FILE_ICONS = {
  'application/pdf':  '📄',
  'image/jpeg':       '🖼',
  'image/png':        '🖼',
  'image/tiff':       '🖼',
  'text/plain':       '📝',
  'text/csv':         '📊',
};

function fileIcon(ct) {
  return FILE_ICONS[ct] || (ct?.includes('word') ? '📝' : ct?.includes('excel') || ct?.includes('sheet') ? '📊' : '📎');
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function AttachmentsWidget({ entityType, entityId }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [error, setError]             = useState('');
  const inputRef = useRef();

  const load = async () => {
    if (!entityId) return;
    try {
      const r = await API.get(`/attachments/${entityType}/${entityId}`);
      setAttachments(r.data);
    } catch { setAttachments([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [entityType, entityId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    const form = new FormData();
    form.append('file', file);
    try {
      await API.post(`/attachments/${entityType}/${entityId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDownload = (att) => {
    // Open download URL in new tab
    const token = JSON.parse(localStorage.getItem('energy_auth') || '{}')?.access_token || '';
    fetch(`/api/attachments/download/${att.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = att.original_name; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`"${att.original_name}" wirklich löschen?`)) return;
    try {
      await API.delete(`/attachments/${att.id}`);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div style={{ marginTop: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>
          Anhänge {attachments.length > 0 && <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({attachments.length})</span>}
        </span>
        <div>
          <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={handleUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.tiff,.txt,.csv" />
          <button className="btn btn-ghost btn-sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? '⏳ Lädt…' : '⬆ Datei hochladen'}
          </button>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 8 }}>{error}</div>}

      {loading && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Lade…</div>}

      {!loading && attachments.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>
          Noch keine Anhänge. PDF, Word, Excel, Bilder erlaubt (max. 20 MB).
        </div>
      )}

      {attachments.map(att => (
        <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 18 }}>{fileIcon(att.content_type)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {att.original_name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {fmtSize(att.file_size)} · {att.uploaded_by}
              {att.created_at && ' · ' + new Date(att.created_at).toLocaleDateString('de-DE')}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => handleDownload(att)}>⬇</button>
          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(att)}>✕</button>
        </div>
      ))}
    </div>
  );
}
